import * as GAME from 'vibegame';
import { Transform, Parent } from 'vibegame/transforms';
import { Renderer } from 'vibegame/rendering';
import { ScaleShaker } from '@blog-components/plugins';

export const MultipleOutputsBlock = GAME.defineComponent({
  index: GAME.Types.ui8,
  baseX: GAME.Types.f32,
  baseY: GAME.Types.f32,
});

export const AverageLine = GAME.defineComponent({});

export const MultipleOutputsManager = GAME.defineComponent({
  initialized: GAME.Types.ui8,
  t: GAME.Types.f32,
  scoresT: GAME.Types.f32,
  averageLineT: GAME.Types.f32,
  sortT: GAME.Types.f32,
  waveScaleT: GAME.Types.f32,
  rowCount: GAME.Types.ui8,
  startX: GAME.Types.f32,
  blockWidth: GAME.Types.f32,
  blockHeight: GAME.Types.f32,
  spacing: GAME.Types.f32,
});

const ROW_COUNT = 4;
const BLOCK_WIDTH = 18;
const BLOCK_HEIGHT = 0.8;
const START_X = 12;
const ROW_SPACING = 2;

const managerQuery = GAME.defineQuery([MultipleOutputsManager]);
const blockQuery = GAME.defineQuery([MultipleOutputsBlock, Transform, Renderer]);
const averageLineQuery = GAME.defineQuery([AverageLine, Transform, Renderer]);

const COLORS = [0xaa4488, 0x4488aa, 0x88aa44, 0xaa8844];
const AVERAGE_LINE_COLOR = 0xffffff;

const MultipleOutputsSetupSystem: GAME.System = {
  group: 'setup',
  update(state) {
    for (const eid of managerQuery(state.world)) {
      if (MultipleOutputsManager.initialized[eid]) continue;
      MultipleOutputsManager.initialized[eid] = 1;

      const rowCount = MultipleOutputsManager.rowCount[eid] || ROW_COUNT;
      const startX = MultipleOutputsManager.startX[eid] || START_X;
      const blockWidth = MultipleOutputsManager.blockWidth[eid] || BLOCK_WIDTH;
      const blockHeight = MultipleOutputsManager.blockHeight[eid] || BLOCK_HEIGHT;
      const spacing = MultipleOutputsManager.spacing[eid] || ROW_SPACING;

      const totalHeight = (rowCount - 1) * spacing;
      const startY = totalHeight / 2;

      for (let i = 0; i < rowCount; i++) {
        const block = state.createEntity();
        const baseX = startX + blockWidth / 2;
        const baseY = startY - i * spacing;

        state.addComponent(block, Transform, {
          posX: baseX,
          posY: baseY,
          posZ: 0,
          scaleX: 0,
          scaleY: 0,
          scaleZ: 0,
        });

        state.addComponent(block, Renderer, {
          shape: 0,
          sizeX: blockWidth,
          sizeY: blockHeight,
          sizeZ: blockHeight,
          color: COLORS[i % COLORS.length],
          visible: 1,
        });

        state.addComponent(block, MultipleOutputsBlock, {
          index: i,
          baseX,
          baseY,
        });

        state.addComponent(block, Parent, { entity: eid });

        if (ABOVE_AVERAGE_INDICES.has(i)) {
          state.addComponent(block, ScaleShaker, {});
        }
      }

      const avgLine = state.createEntity();
      const avgX = startX + blockWidth / 2;
      state.addComponent(avgLine, Transform, {
        posX: avgX,
        posY: 0,
        posZ: -0.5,
        scaleX: 0,
        scaleY: 0,
        scaleZ: 0,
      });
      state.addComponent(avgLine, Renderer, {
        shape: 0,
        sizeX: blockWidth + 2,
        sizeY: 0.15,
        sizeZ: 0.15,
        color: AVERAGE_LINE_COLOR,
        visible: 1,
      });
      state.addComponent(avgLine, AverageLine, {});
      state.addComponent(avgLine, Parent, { entity: eid });
    }
  },
};

function backOut(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function hslToRgb(h: number, s: number, l: number): number {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const ri = Math.round((r + m) * 255);
  const gi = Math.round((g + m) * 255);
  const bi = Math.round((b + m) * 255);

  return (ri << 16) | (gi << 8) | bi;
}

const SCORE_VALUES = [0.2, 0.85, 0.6, 0.3];
const AVERAGE_SCORE = SCORE_VALUES.reduce((a, b) => a + b, 0) / SCORE_VALUES.length;
const ABOVE_AVERAGE_INDICES = new Set(
  SCORE_VALUES.map((score, i) => (score > AVERAGE_SCORE ? i : -1)).filter((i) => i >= 0)
);

const SORTED_INDICES = SCORE_VALUES.map((score, i) => ({ score, i }))
  .sort((a, b) => b.score - a.score)
  .map((item) => item.i);

const INDEX_TO_SORTED_POSITION: number[] = [];
for (let sortedPos = 0; sortedPos < SORTED_INDICES.length; sortedPos++) {
  const originalIndex = SORTED_INDICES[sortedPos];
  INDEX_TO_SORTED_POSITION[originalIndex] = sortedPos;
}

const MultipleOutputsAnimationSystem: GAME.System = {
  group: 'simulation',
  update(state) {
    let managerT = 0;
    let scoresT = 0;
    let averageLineT = 0;
    let sortT = 0;
    let spacing = ROW_SPACING;
    let rowCount = ROW_COUNT;
    for (const eid of managerQuery(state.world)) {
      managerT = MultipleOutputsManager.t[eid];
      scoresT = MultipleOutputsManager.scoresT[eid];
      averageLineT = MultipleOutputsManager.averageLineT[eid];
      sortT = MultipleOutputsManager.sortT[eid];
      spacing = MultipleOutputsManager.spacing[eid] || ROW_SPACING;
      rowCount = MultipleOutputsManager.rowCount[eid] || ROW_COUNT;
    }

    const totalHeight = (rowCount - 1) * spacing;
    const startY = totalHeight / 2;

    for (const eid of blockQuery(state.world)) {
      const index = MultipleOutputsBlock.index[eid];
      const baseY = MultipleOutputsBlock.baseY[eid];

      const appearDelay = index * 0.12;
      const particleT = Math.max(0, Math.min(1, (managerT - appearDelay) / 0.4));

      const scale = backOut(particleT);
      Transform.scaleX[eid] = scale;
      Transform.scaleY[eid] = scale;
      Transform.scaleZ[eid] = scale;

      const sortedPosition = INDEX_TO_SORTED_POSITION[index];
      const sortedY = startY - sortedPosition * spacing;
      const easedSortT = backOut(sortT);
      Transform.posY[eid] = baseY + (sortedY - baseY) * easedSortT;

      const baseColor = COLORS[index % COLORS.length];
      const scoreValue = SCORE_VALUES[index % SCORE_VALUES.length];
      const hue = scoreValue * 120;
      const scoreColor = hslToRgb(hue, 0.8, 0.5);

      const r1 = (baseColor >> 16) & 0xff;
      const g1 = (baseColor >> 8) & 0xff;
      const b1 = baseColor & 0xff;
      const r2 = (scoreColor >> 16) & 0xff;
      const g2 = (scoreColor >> 8) & 0xff;
      const b2 = scoreColor & 0xff;

      const r = Math.round(r1 + (r2 - r1) * scoresT);
      const g = Math.round(g1 + (g2 - g1) * scoresT);
      const b = Math.round(b1 + (b2 - b1) * scoresT);

      Renderer.color[eid] = (r << 16) | (g << 8) | b;
    }

    for (const eid of averageLineQuery(state.world)) {
      const scale = backOut(averageLineT);
      Transform.scaleX[eid] = scale;
      Transform.scaleY[eid] = scale;
      Transform.scaleZ[eid] = scale;
    }
  },
};

export const MultipleOutputsPlugin: GAME.Plugin = {
  components: { MultipleOutputsBlock, MultipleOutputsManager, AverageLine },
  systems: [MultipleOutputsSetupSystem, MultipleOutputsAnimationSystem],
  config: {
    defaults: {
      'multiple-outputs-manager': {
        initialized: 0,
        t: 0,
        scoresT: 0,
        averageLineT: 0,
        sortT: 0,
        rowCount: ROW_COUNT,
        startX: START_X,
        blockWidth: BLOCK_WIDTH,
        blockHeight: BLOCK_HEIGHT,
        spacing: ROW_SPACING,
      },
    },
  },
};
